import * as t from 'io-ts'
import { Lens } from 'monocle-ts'
import { Intl } from './intl'
import { MultiLocaleString } from './multi-locale-string'
import { mkPercent, Percent, unPercent } from './percent'
import * as F from 'fp-ts'

// ----------------------------------------------------------------------------

export const Projection = t.type({
  detail: t.union([t.undefined, t.null, MultiLocaleString]),
  votes: t.type({
    yea: t.union([t.null, Percent]),
    nay: t.union([t.null, Percent]),
  }),
})
export type Projection = t.TypeOf<typeof Projection>

export const emptyProjection: Projection = {
  detail: {},
  votes: {
    yea: null,
    nay: null,
  },
}

export const _projectionDetail = Lens.fromProp<Projection>()('detail')
export const _projectionYea = Lens.fromPath<Projection>()(['votes', 'yea'])
export const _projectionNay = Lens.fromPath<Projection>()(['votes', 'yea'])

// ----------------------------------------------------------------------------

export const AbsoluteAndPercent = t.type({
  absolute: t.union([t.null, t.number]),
  percent: t.union([t.null, Percent]),
})
export type AbsoluteAndPercent = t.TypeOf<typeof AbsoluteAndPercent>

export const eqAbsoluteAndPercent: F.eq.Eq<AbsoluteAndPercent> = F.eq.getStructEq({
  absolute: F.eq.fromEquals((a, b) => a === b),
  percent: F.eq.fromEquals((a, b) => a === b),
})

export const _absolute = Lens.fromProp<AbsoluteAndPercent>()('absolute')
export const _percent = Lens.fromProp<AbsoluteAndPercent>()('percent')

// ----------------------------------------------------------------------------

export const Votes = t.type({
  yea: AbsoluteAndPercent,
  nay: AbsoluteAndPercent,
})
export type Votes = t.TypeOf<typeof Votes>

export const eqVotes: F.eq.Eq<Votes> = F.eq.getStructEq({
  yea: eqAbsoluteAndPercent,
  nay: eqAbsoluteAndPercent,
})

export const _yea = Lens.fromProp<Votes>()('yea')
export const _nay = Lens.fromProp<Votes>()('nay')

// ----------------------------------------------------------------------------

export const Result = t.type({
  // This is stored as a Boolean in the database but its exact meaning depends on
  // the type of ballot: for standalone ballots true means Outcome 'yea' and false
  // means Outcome 'nay', but for tiebreaker questions (Stichfrage) true means
  // the initiative is preferred and false means the counter-proposal is preferred.
  accepted: t.union([t.undefined, t.null, t.boolean]),

  // Number of votes cast, both in absolute number and in percent.
  voterTurnout: AbsoluteAndPercent,

  // Number of valid votes cast. This excludes empty and invalid ballots.
  // Meaning, votes.yea.absolute + votes.nay.absolute may be less than
  // voterTurnout.absolute!
  votes: Votes,
})
export type Result = t.TypeOf<typeof Result>

export const eqResult: F.eq.Eq<Result> = F.eq.getStructEq({
  accepted: F.eq.fromEquals((a, b) => a === b),
  voterTurnout: eqAbsoluteAndPercent,
  votes: eqVotes,
})

/**
 * Return true if the result is unset (all fields null).
 *
 * The 'accepted' field is excluded, because the backoffice UI does not
 * provide any means to reset it. User can only reset the other fields.
 */
export function isEmptyResult(result: Result): boolean {
  return eqResult.equals({ ...result, accepted: null }, emptyResult)
}

export const emptyResult: Result = {
  accepted: null,
  voterTurnout: {
    absolute: null,
    percent: null,
  },
  votes: {
    yea: {
      absolute: null,
      percent: null,
    },
    nay: {
      absolute: null,
      percent: null,
    },
  },
}

export const _accepted = Lens.fromProp<Result>()('accepted')
export const _voterTurnout = Lens.fromProp<Result>()('voterTurnout')
export const _votes = Lens.fromProp<Result>()('votes')

// ----------------------------------------------------------------------------

export class UIPopulationCountType extends t.Type<null | number, string, string> {
  constructor(intl: Intl) {
    super(
      'UIPopulationCountType',
      (v: unknown): v is number => this._parse(v) !== undefined,
      (s: string, c: t.Context): t.Validation<null | number> => {
        if (s === '') {
          return t.success(null)
        } else {
          const v = this._parse(s)
          return v !== undefined ? t.success(v) : t.failure(s, c)
        }
      },
      (a: null | number): string => (a === null ? '' : intl.formatNumber(a)),
    )
  }

  _parse = (v: unknown): undefined | number => {
    if (typeof v === 'string') {
      const n = parseFloat(v.replace(/[^0-9]/g, ''))
      return isNaN(n) ? undefined : n
    } else {
      return undefined
    }
  }
}

// ----------------------------------------------------------------------------

/**
 * A type which converts between 'string' and 'Percent'. Rounds to one decimal
 * place upon parsing!
 */

export class UIPercentType1 extends t.Type<null | Percent, string, string> {
  constructor(intl: Intl) {
    super(
      'UIPercentType1',
      (v: unknown): v is Percent => this._parse(v) !== undefined,
      (s: string, c: t.Context): t.Validation<null | Percent> => {
        if (s === '') {
          return t.success(null)
        } else {
          const v = this._parse(s)
          return v !== undefined ? t.success(v) : t.failure(s, c)
        }
      },
      (a: null | Percent): string => {
        return a === null
          ? ''
          : intl.formatNumber(unPercent(a) * 100, {
              style: 'decimal',
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            })
      },
    )
  }

  _parse = (v: unknown): undefined | Percent => {
    if (typeof v === 'string') {
      /*
       * When parsing the string, we allow both '.' and ',' as the decimal separator.
       * This is to allow pasting strings copied from other sources which may use
       * either of those characters as decimal separator.
       */
      const n = parseFloat(v.replace(',', '.'))

      return isNaN(n) || n < 0 || n > 100 ? undefined : mkPercent(Math.round(n * 10) / 1000)
    } else {
      return undefined
    }
  }
}

// ----------------------------------------------------------------------------

export class UIString extends t.Type<null | string, string, string> {
  constructor() {
    super(
      'UIString',
      (v: unknown): v is string => this._parse(v) !== undefined,
      (s: string, c: t.Context): t.Validation<null | string> => {
        if (s === '') {
          return t.success(null)
        } else {
          const v = this._parse(s)
          return v !== undefined ? t.success(v) : t.failure(s, c)
        }
      },
      (a: null | string): string => {
        return a === null ? '' : a
      },
    )
  }

  _parse = (v: unknown): undefined | string => {
    if (typeof v === 'string') {
      return v
    } else {
      return undefined
    }
  }
}

// ----------------------------------------------------------------------------

export class UIPosition extends t.Type<null | number, string, string> {
  constructor() {
    super(
      'UIPosition',
      (v: unknown): v is number => this._parse(v) !== undefined,
      (s: string, c: t.Context): t.Validation<null | number> => {
        if (s === '') {
          return t.success(null)
        } else {
          const v = this._parse(s)
          return v !== undefined ? t.success(v) : t.failure(s, c)
        }
      },
      (a: null | number): string => (a === null ? '' : `${a}`),
    )
  }

  _parse = (v: unknown): undefined | number => {
    if (typeof v === 'string') {
      const n = parseFloat(v.replace(/[^0-9]/g, ''))
      return isNaN(n) ? undefined : n
    } else {
      return undefined
    }
  }
}

// ----------------------------------------------------------------------------

export class UIInteger extends t.Type<null | number, string, string> {
  constructor(intl: Intl) {
    super(
      'UIInteger',
      (v: unknown): v is number => this._parse(v) !== undefined,
      (s: string, c: t.Context): t.Validation<null | number> => {
        if (s === '') {
          return t.success(null)
        } else {
          const v = this._parse(s)
          return v !== undefined ? t.success(v) : t.failure(s, c)
        }
      },
      (a: null | number): string => (a === null ? '' : intl.formatNumber(a)),
    )
  }

  _parse = (v: unknown): undefined | number => {
    if (typeof v === 'string') {
      const n = parseFloat(v.replace(/[^0-9-]/g, ''))
      return isNaN(n) ? undefined : n
    } else {
      return undefined
    }
  }
}
